import { CashierPreferenceLevel, SizeType } from '../enumerations/cashier-preference-level';
import { ILoginUser } from '../interfaces/dtos/ILoginUser';
import { store } from '../redux/configure-store';
import { UserPreferences, UserPreferencesDictionary } from './config-parameters-handler';
import { decrypt, encrypt } from './encrypt-helper';

export const COMPUTER_NAME = 'computerName';
export const MESSAGE_ID = 'messageId';
export const STORE_ID = 'storeId';
export const TOUCHPOINT_ID = 'touchpointId';
export const TOUCHPOINT_GROUP_ID = 'touchpointGroupId';
export const STORED_BASE_URL = 'storedBaseUrl';
export const LOCK_USER = 'lockUserName';
export const USER_PREFERENCES = 'userPref';
export const OPEN_ID_CONFIG = 'openIdConfig';
/* remember to update the UserPreferences interface in config-parameters-handler.ts if you add more defaults */
export const USER_DEFAULT_PREFERENCES = {
  productWindowKeypadVisible: true,
  actionWindowKeypadVisible: true,
  productWindowQuickViewVisible: false,
  accessibilityPreferences: {
    leftHanded: false,
    contrastLevel: CashierPreferenceLevel.Zero,
    textZoomLevel: SizeType.Medium,
    buttonZoomLevel: SizeType.Medium,
  },
};

export abstract class LocalStorage {
  public static computerName(): string | undefined {
    const computerName = localStorage.getItem(COMPUTER_NAME);

    if (computerName) {
      return decrypt(computerName);
    }

    return undefined;
  }

  public static setComputerName(computerName: string): void {
    localStorage.setItem(COMPUTER_NAME, encrypt(computerName));
  }

  public static removeComputerName(): void {
    localStorage.removeItem(COMPUTER_NAME);
  }

  public static setStoreId(storeId: string): void {
    localStorage.setItem(STORE_ID, storeId);
  }

  public static storeId(): string {
    return localStorage.getItem(STORE_ID) || '';
  }

  public static setTillId(tillId: string): void {
    localStorage.setItem(TOUCHPOINT_ID, tillId);
  }

  public static tillId(): string {
    return localStorage.getItem(TOUCHPOINT_ID) || '';
  }

  public static setTouchpointGroupId(touchpointGroupId: string): void {
    localStorage.setItem(TOUCHPOINT_GROUP_ID, touchpointGroupId);
  }

  public static touchpointGroupId(): string {
    return localStorage.getItem(TOUCHPOINT_GROUP_ID) || '';
  }

  public static messageId(): string {
    return localStorage.getItem(MESSAGE_ID) || '0';
  }

  public static setMessageId(messageId: string): void {
    localStorage.setItem(MESSAGE_ID, messageId);
  }

  public static storedBaseUrl(): string {
    return localStorage.getItem(STORED_BASE_URL) || '';
  }

  public static setStoredBaseUrl(storedBaseUrl: string): void {
    localStorage.setItem(STORED_BASE_URL, storedBaseUrl);
  }

  public static clearLocalStorage(exceptKeys?: string[]): void {
    if (exceptKeys) {
      const savedValues = new Map<string, string | null>();
      exceptKeys.forEach(value => {
        savedValues.set(value, localStorage.getItem(value));
      });
      localStorage.clear();
      savedValues.forEach((value, key) => {
        if (value) {
          localStorage.setItem(key, value);
        }
      });
    } else {
      localStorage.clear();
    }
  }

  public static getLockUser(): string {
    let userName: string = '';
    if (this.getLockUserObject()) {
      userName = this.getLockUserObject().userName || '';
    }

    return userName;
  }

  public static setLockUser(userName: string, trainingMode: boolean): void {
    if (store.getState().user.sessionId) {
      const { sessionId, displayName, workerId } = store.getState().user;
      const encryptedSessionId = encrypt(sessionId ?? '');
      const user = JSON.stringify({
        userName,
        sessionId: encryptedSessionId,
        displayName,
        workerId,
        trainingMode,
      });

      localStorage.setItem(LOCK_USER, user);
    }
  }

  public static removeLockUser(): void {
    localStorage.removeItem(LOCK_USER);
  }

  public static getLockUserObject(): ILoginUser {
    let loginUser: ILoginUser = {};

    if (localStorage.getItem(LOCK_USER)) {
      loginUser = JSON.parse(localStorage.getItem(LOCK_USER) || '') as ILoginUser;
    }

    return loginUser;
  }

  public static getLockUserToken(): string {
    let newToken: string = '';
    const { sessionId } = this.getLockUserObject();

    if (sessionId) {
      newToken = decrypt(sessionId);
    }

    return newToken;
  }

  public static getLockUserDisplayName(): string {
    const { displayName } = this.getLockUserObject();

    if (displayName) {
      return displayName;
    }

    return '';
  }

  /**
   * returns the logged in users preferences
   */
  public static getUserPreferences(): UserPreferences {
    const userName = store.getState().user.userName || 'Default';
    const allPref = JSON.parse(localStorage.getItem(USER_PREFERENCES) as string) || {};
    if (!allPref[userName]) {
      /* return early if possible */
      return this.setDefaultUserPreferences(userName, allPref);
    }
    return allPref[userName];
  }
  /**
   * updates the logged in users preferences
   * @param preferences UserPreferences { key: value }
   */
  public static setUserPreferences(preferences: Partial<UserPreferences>): void {
    const userName = store.getState().user.userName || 'Default';
    const allPref = JSON.parse(localStorage.getItem(USER_PREFERENCES) as string) || {};
    if (!allPref[userName]) {
      /* do not return early here */
      this.setDefaultUserPreferences(userName, allPref);
    }
    allPref[userName] = { ...allPref[userName], ...preferences };
    localStorage.setItem('userPref', JSON.stringify(allPref));
  }
  /**
   * the default user preferences
   * @param userName string
   * @param allPref UserPreferencesDictionary
   */
  private static setDefaultUserPreferences(
    userName: string,
    allPref: UserPreferencesDictionary,
  ): UserPreferences {
    /* the default preferences are defined at the top of this file */
    allPref[userName] = USER_DEFAULT_PREFERENCES;
    localStorage.setItem(USER_PREFERENCES, JSON.stringify(allPref));
    return allPref[userName];
  }

  public static setOpenIdConfig(config: any): void {
    localStorage.setItem(OPEN_ID_CONFIG, JSON.stringify(config));
  }

  public static getOpenIdConfig(): any {
    return JSON.parse(localStorage.getItem(OPEN_ID_CONFIG) as string) || undefined;
  }
}
